/* ==========================================================================
   Keynote theme — global.js
   Scroll reveals, hero scale, horizontal scrollers, (+) popovers,
   product form (variants / gallery / sticky ATC), AJAX cart count.
   ========================================================================== */

(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Reveal on scroll ---------- */
  function initReveals() {
    const els = document.querySelectorAll('.reveal');
    if (!els.length) return;
    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );
    els.forEach((el) => io.observe(el));

    document.querySelectorAll('.reveal-stagger').forEach((group) => {
      Array.from(group.children).forEach((child, i) => {
        child.style.setProperty('--stagger-i', i);
      });
    });
  }

  /* ---------- Header scroll state ---------- */
  function initHeader() {
    const header = document.querySelector('.site-header');
    if (!header || header.dataset.bound) return;
    header.dataset.bound = '1';
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    const toggle = header.querySelector('.menu-toggle');
    if (toggle) {
      toggle.addEventListener('click', () => {
        const open = header.classList.toggle('menu-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
  }

  /* ---------- Hero subtle scale on scroll ---------- */
  function initHeroParallax() {
    if (prefersReducedMotion || window.__heroParallax) return;
    window.__heroParallax = true;
    const update = () => {
      document.querySelectorAll('.hero[data-parallax="true"] .hero__media').forEach((media) => {
        const rect = media.parentElement.getBoundingClientRect();
        const progress = Math.min(Math.max(-rect.top / Math.max(rect.height, 1), 0), 1);
        media.style.setProperty('--hero-scale', (1 + progress * 0.08).toFixed(4));
      });
    };
    update();
    window.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
  }

  /* ---------- Horizontal scrollers ---------- */
  function initScrollers() {
    document.querySelectorAll('[data-scroller]').forEach((scroller) => {
      if (scroller.dataset.bound) return;
      scroller.dataset.bound = '1';
      const track = scroller.querySelector('.scroller__track');
      const prev = scroller.querySelector('[data-scroll-prev]');
      const next = scroller.querySelector('[data-scroll-next]');
      if (!track) return;

      const step = () => {
        const card = track.querySelector('.feature-card, .product-card, .vt-card');
        return card ? card.getBoundingClientRect().width + 20 : track.clientWidth * 0.8;
      };

      const updateArrows = () => {
        if (prev) prev.disabled = track.scrollLeft <= 4;
        if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      };

      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', () => window.requestAnimationFrame(updateArrows), { passive: true });
      window.addEventListener('resize', updateArrows);
      updateArrows();
    });
  }

  /* ---------- (+) buttons → info popovers ---------- */
  function initPlusButtons() {
    const closeAll = (except) => {
      document.querySelectorAll('.info-card.is-open').forEach((card) => {
        if (card !== except) {
          card.classList.remove('is-open');
          const btn = document.querySelector('[aria-controls="' + card.id + '"]');
          if (btn) btn.setAttribute('aria-expanded', 'false');
        }
      });
    };

    document.querySelectorAll('.plus-button[aria-controls]').forEach((btn) => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      const card = document.getElementById(btn.getAttribute('aria-controls'));
      if (!card) return;
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const willOpen = !card.classList.contains('is-open');
        closeAll(willOpen ? card : null);
        card.classList.toggle('is-open', willOpen);
        btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      });
    });

    if (!window.__plusGlobal) {
      window.__plusGlobal = true;
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.info-card')) closeAll(null);
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeAll(null);
      });
    }
  }

  /* ---------- Product gallery ---------- */
  function initGallery() {
    document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.bound) return;
      gallery.dataset.bound = '1';
      const main = gallery.querySelector('.product-gallery__main img');
      const thumbs = gallery.querySelectorAll('.product-gallery__thumb');
      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          thumbs.forEach((t) => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
          if (main) {
            main.src = thumb.dataset.src;
            main.srcset = thumb.dataset.srcset || '';
            main.alt = thumb.querySelector('img') ? thumb.querySelector('img').alt : '';
          }
        });
      });
    });
  }

  /* ---------- Quantity steppers ---------- */
  function initQuantity() {
    document.querySelectorAll('.quantity-input').forEach((wrap) => {
      if (wrap.dataset.bound) return;
      wrap.dataset.bound = '1';
      const input = wrap.querySelector('input');
      wrap.querySelectorAll('button').forEach((btn) => {
        btn.addEventListener('click', () => {
          const delta = btn.dataset.step === 'up' ? 1 : -1;
          const next = Math.max(1, (parseInt(input.value, 10) || 1) + delta);
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ---------- Product form: variants + AJAX add-to-cart ---------- */
  function initProductForms() {
    document.querySelectorAll('[data-product-form]').forEach((container) => {
      if (container.dataset.bound) return;
      container.dataset.bound = '1';
      const jsonEl = container.querySelector('[data-product-json]');
      if (!jsonEl) return;
      const product = JSON.parse(jsonEl.textContent);
      const form = container.querySelector('form[data-type="add-to-cart-form"]');
      const idInput = form ? form.querySelector('input[name="id"]') : null;
      const priceEl = container.querySelector('[data-price]');
      const atcBtn = container.querySelector('[data-add-to-cart]');
      const atcText = atcBtn ? atcBtn.querySelector('[data-add-to-cart-text]') : null;

      const money = (cents) => {
        const amount = (cents / 100).toFixed(2);
        return (window.Shopify && window.Shopify.currency ? '' : '$') + amount + (window.Shopify && window.Shopify.currency ? ' ' + window.Shopify.currency.active : '');
      };

      const selectedOptions = () =>
  Array.from(
    container.querySelectorAll('.variant-fieldset input[type="radio"]:checked')
  ).map((input) => input.value);

const findVariant = () => {
  const opts = selectedOptions();

  // Products with only Shopify's default variant
  if (!opts.length) {
    return (
      product.variants.find(
        (v) => idInput && String(v.id) === String(idInput.value)
      ) || product.variants[0]
    );
  }

  return product.variants.find((v) =>
    v.options.every((o, i) => o === opts[i])
  );
};

      const updateState = () => {
        const variant = findVariant();
        if (!variant) {
          if (atcBtn) { atcBtn.disabled = true; }
          if (atcText) atcText.textContent = container.dataset.textUnavailable || 'Unavailable';
          return;
        }
        if (idInput) idInput.value = variant.id;
        if (priceEl) priceEl.textContent = money(variant.price);
        if (atcBtn) atcBtn.disabled = !variant.available;
        if (atcText) {
          atcText.textContent = variant.available
            ? container.dataset.textAdd || 'Add to Bag'
            : container.dataset.textSoldout || 'Sold out';
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      };

      container.querySelectorAll('.variant-fieldset input').forEach((input) => {
        input.addEventListener('change', updateState);
      });

      if (form) {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          if (atcBtn) atcBtn.disabled = true;
          if (atcText) atcText.textContent = container.dataset.textAdding || 'Adding…';
          try {
            const res = await fetch(window.routes.cart_add_url + '.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                id: parseInt(idInput.value, 10),
                quantity: parseInt(form.querySelector('input[name="quantity"]').value, 10) || 1
              })
            });
            if (!res.ok) throw new Error('add failed');
            await refreshCartCount();
            if (atcText) atcText.textContent = container.dataset.textAdded || 'Added ✓';
            setTimeout(updateState, 1600);
          } catch (err) {
            if (atcText) atcText.textContent = window.cartStrings.error || 'Error';
            setTimeout(updateState, 2000);
          }
          if (atcBtn) atcBtn.disabled = false;
        });
      }

      updateState();
    });
  }

  async function refreshCartCount() {
    try {
      const res = await fetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count > 0 ? cart.item_count : '';
      });
    } catch (e) { /* noop */ }
  }

  /* ---------- Sticky add-to-cart bar ---------- */
  function initStickyAtc() {
    const bar = document.querySelector('.sticky-atc');
    const anchor = document.querySelector('[data-atc-anchor]');
    if (!bar || !anchor || bar.dataset.bound || !('IntersectionObserver' in window)) return;
    bar.dataset.bound = '1';
    const io = new IntersectionObserver(
      (entries) => {
        bar.classList.toggle('is-visible', !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );
    io.observe(anchor);

    const barBtn = bar.querySelector('[data-sticky-add]');
    const mainForm = document.querySelector('[data-product-form] form[data-type="add-to-cart-form"]');
    if (barBtn && mainForm) {
      barBtn.addEventListener('click', () => mainForm.requestSubmit());
    }
  }

  /* ---------- Cart page quantity/remove ---------- */
  function initCartPage() {
    const cartRoot = document.querySelector('[data-cart-page]');
    if (!cartRoot || cartRoot.dataset.bound) return;
    cartRoot.dataset.bound = '1';

    const change = async (line, quantity) => {
      const res = await fetch(window.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      });
      if (res.ok) window.location.reload();
    };

    cartRoot.querySelectorAll('[data-cart-remove]').forEach((btn) => {
      btn.addEventListener('click', () => change(parseInt(btn.dataset.line, 10), 0));
    });
    cartRoot.querySelectorAll('.cart-item .quantity-input input').forEach((input) => {
      input.addEventListener('change', () =>
        change(parseInt(input.dataset.line, 10), Math.max(0, parseInt(input.value, 10) || 0))
      );
    });
  }

  function initAll() {
    initReveals();
    initHeader();
    initHeroParallax();
    initScrollers();
    initPlusButtons();
    initGallery();
    initQuantity();
    initProductForms();
    initStickyAtc();
    initCartPage();
  }

  document.addEventListener('DOMContentLoaded', initAll);
  /* Re-initialize when sections are added/re-rendered in the theme editor */
  document.addEventListener('shopify:section:load', initAll);
})();
